import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './state-colour-picker-dialog.styles.css?inline';
import {
  STATE_PALETTE_FAMILIES,
  type StatePaletteColour,
} from '../../../utility/state-colours/state-colour-registry';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import type SlDialog from '@shoelace-style/shoelace/dist/components/dialog/dialog.js';

/** What the picker is recolouring - carried through so the host knows what was picked for. */
export interface StateColourPickTarget {
  kind: 'label' | 'use';
  /** labelKey for kind 'label'. */
  labelKey?: string;
  /** domainKey + valueKey for kind 'use'. */
  domainKey?: string;
  valueKey?: string;
  /** The word shown in the dialog header so the admin sees what they are recolouring. */
  displayLabel: string;
  /** The colour currently in effect, ringed in the grid. */
  currentColour: StatePaletteColour;
}

/**
 * The palette dialog: every colour, grouped by hue family, as clickable light|dark swatches
 * (the architect's requested shape, 2026-09-04: "a change button on each row that opens a
 * dialog showing the palette"). Clicking a swatch IS the choice - a dialog for a single-value
 * pick needs no confirm step, and the row's live preview is the feedback.
 *
 * Emits `colour-picked` with the target and colour; the HOST saves. The dialog never calls
 * the API itself - portability rule: components emit intent - and stays open showing
 * `errorMessage` when the host reports a failure, so a conflict does not eat the choice.
 */
@customElement('state-colour-picker-dialog')
export class StateColourPickerDialog extends LitElement {
  static styles = unsafeCSS(styles);

  @state() private target?: StateColourPickTarget;

  /** Set by the host after a failed save; cleared on the next open or pick. */
  @property() errorMessage = '';

  /** Disables the swatches while the host's save is in flight. */
  @property({ type: Boolean }) saving = false;

  /** Open for a target. The host calls this from each row's Change button. */
  show(target: StateColourPickTarget): void {
    this.target = target;
    this.errorMessage = '';
    void this.updateComplete.then(() => this.dialog?.show());
  }

  hide(): void {
    void this.dialog?.hide();
  }

  private get dialog(): SlDialog | null {
    return this.renderRoot.querySelector('sl-dialog');
  }

  private pick(colour: StatePaletteColour): void {
    if (!this.target || this.saving) return;
    this.errorMessage = '';
    this.dispatchEvent(
      new CustomEvent('colour-picked', {
        detail: { target: this.target, colour },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    if (!this.target) return nothing;
    return html`
      <sl-dialog label="Colour for ${this.target.displayLabel}">
        <p class="hint">
          Pick from the palette - each colour is one choice, authored for light
          and dark together. The change lands on every screen immediately.
        </p>
        ${
          this.errorMessage
            ? html`<p class="error" role="alert">${this.errorMessage}</p>`
            : nothing
        }
        <div class="families" ?aria-busy=${this.saving}>
          ${STATE_PALETTE_FAMILIES.map(
            ({ family, colours }) => html`
              <div class="family">
                <span class="family-name">${family}</span>
                <div class="swatches">
                  ${colours.map(
                    (colour) => html`
                      <button
                        class="swatch"
                        type="button"
                        ?disabled=${this.saving}
                        aria-pressed=${colour === this.target!.currentColour}
                        title=${colour}
                        @click=${() => this.pick(colour)}
                      >
                        <span class="duo">
                          <i
                            style="--sw-fill: var(--agwa-state-${colour}-light)"
                          ></i>
                          <i
                            style="--sw-fill: var(--agwa-state-${colour}-dark)"
                          ></i>
                        </span>
                        <span class="swatch-name">${colour}</span>
                      </button>
                    `
                  )}
                </div>
              </div>
            `
          )}
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'state-colour-picker-dialog': StateColourPickerDialog;
  }
}
